import { useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';

import { PAGES } from '../../constants/pages';

import {
    BundleState,
    useBundleUpdatedSubscription,
    useGetBundlesFilterQuery,
    useUpdateBundleStateMutation,
    type BundlesFilterInput,
} from 'data/__generated__';

import { DataView, type DataViewSelection } from 'components/DataView';
import { PageBreadcrumb, PageBreadcrumbSegment, PageHeader } from 'components/PageHeader';

import { BundlesSummaryHeader } from './components/BundlesSummaryHeader';
import { OrphanBoxesWarning } from './components/OrphanBoxesWarning';
import { buildBundleActions } from './constants/bundleActions';
import {
    BUNDLES_PAGE_SIZE,
    BUNDLE_FILTERS,
    DEFAULT_BUNDLE_FILTERS,
    toBundlesFilterInput,
} from './constants/bundleFilters';
import { buildBundleViews } from './constants/bundleViews';
import type { BundleRow } from './constants/bundlesColumns';
import { BUNDLE_STATES_LABELS } from './constants/states';

export const Bundles = () => {
    const [filters, setFilters] = useState<BundlesFilterInput>(DEFAULT_BUNDLE_FILTERS);

    const handleFiltersChange = useCallback((filterState: Record<string, unknown>) => {
        const next = toBundlesFilterInput(filterState);
        // La `DataView` remonte son état à chaque rendu : sans cette comparaison, la query repartirait
        // en boucle sur une valeur pourtant identique.
        setFilters((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    }, []);

    const {
        data: bundlesData,
        loading: bundlesLoading,
        error: bundlesError,
        fetchMore,
    } = useGetBundlesFilterQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: { offset: 0, limit: BUNDLES_PAGE_SIZE, filters },
    });

    useBundleUpdatedSubscription();

    const bundles = useMemo(() => bundlesData?.bundlesFilter.bundles ?? [], [bundlesData]);
    const hasMore = bundlesData?.bundlesFilter.hasMore ?? false;

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loadMore = useCallback(async () => {
        setIsLoadingMore(true);
        try {
            await fetchMore({ variables: { offset: bundles.length, limit: BUNDLES_PAGE_SIZE, filters } });
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchMore, bundles.length, filters]);

    const [updateBundleState, { loading: updateStateLoading }] = useUpdateBundleStateMutation();

    const handleUpdateState = useCallback(
        async (newState: BundleState, selectedBundles: BundleRow[], selection: DataViewSelection) => {
            if (!selectedBundles.length) {
                return;
            }

            const results = await Promise.all(
                selectedBundles.map(({ _id }) => updateBundleState({ variables: { bundleId: _id, state: newState } })),
            );

            const failures = results.filter((result) => !result.data || result.data.updateBundleState.error);
            const successCount = selectedBundles.length - failures.length;
            const stateLabel = BUNDLE_STATES_LABELS[newState] ?? newState;

            if (successCount) {
                toast.success(`${successCount} bundle(s) passé(s) à "${stateLabel}"`);
            }
            if (failures.length) {
                toast.error(`${failures.length} bundle(s) sur ${selectedBundles.length} n'ont pas pu être mis à jour`);
            }

            // Une fois les bundles passés dans l'état suivant, l'action qui les a traités ne s'applique
            // plus à eux : la sélection repart à vide.
            selection.setSelectedKeys([]);
        },
        [updateBundleState],
    );

    const views = useMemo(
        () => buildBundleViews({ hasMore, isLoadingMore, onLoadMore: loadMore }),
        [hasMore, isLoadingMore, loadMore],
    );

    const actions = useMemo(
        () => buildBundleActions({ onUpdateState: handleUpdateState, isUpdatingState: updateStateLoading }),
        [handleUpdateState, updateStateLoading],
    );

    const renderSummary = useCallback(
        ({ rows, selection }: { rows: BundleRow[]; selection: DataViewSelection }) => (
            <BundlesSummaryHeader
                bundles={rows}
                selectedIds={selection.selectedKeys}
                onSelectionChange={selection.setSelectedKeys}
            />
        ),
        [],
    );

    if (bundlesError) {
        throw new Error('Une erreur est survenue lors de la récupération des bundles');
    }

    return (
        <Container>
            <Helmet title={`${PAGES.bundles.label} · TOTADMIN`} />
            <PageHeader>
                <PageBreadcrumb>
                    <PageBreadcrumbSegment label={PAGES.bundles.label} isCurrent />
                </PageBreadcrumb>
            </PageHeader>
            <OrphanBoxesWarning />
            <Content>
                <DataView
                    data={bundles}
                    getRowId={({ _id }) => _id}
                    isLoading={bundlesLoading && !bundlesData}
                    filters={BUNDLE_FILTERS}
                    onFiltersChange={handleFiltersChange}
                    // Pas d'`accessor` : la recherche part au SERVEUR. Sur une liste paginée, chercher
                    // dans les lignes chargées ne regarderait que la page en cours.
                    search={{ placeholder: 'Numéro de bundle ou nom du site' }}
                    views={views}
                    actions={actions}
                    renderSummary={renderSummary}
                />
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
